// Home board: family notes, countdowns ("sleeps until"), the nightly
// checklist and the weekly family summary. Types and validators shared by
// the Worker and the wall.

import { addDays, compareIsoDate, parseIsoDate, type IsoDate } from "./dates";
import { isCivilDate } from "./kitchen/validate";

export const NOTE_COLORS = ["yellow", "pink", "blue", "green", "orange", "purple"] as const;
export type NoteColor = (typeof NOTE_COLORS)[number];

export interface FamilyNote {
  id: string;
  text: string;
  color: NoteColor;
  forMemberId: string | null;
  authorId: string | null;
  expiresOn: IsoDate | null;
  createdAt: string;
  updatedAt: string;
}

export interface NoteInput {
  text: string;
  color: NoteColor;
  forMemberId: string | null;
  expiresOn: IsoDate | null;
}

export interface Countdown {
  id: string;
  title: string;
  date: IsoDate;
  emoji: string;
  createdAt: string;
}

export interface CountdownInput {
  title: string;
  date: IsoDate;
  emoji: string;
}

export interface NightlyItem {
  id: string;
  label: string;
  icon: string;
  sortOrder: number;
}

export interface NightlyCheck {
  itemId: string;
  checkedAt: string;
  checkedBy: string | null;
}

export interface NightlyPayload {
  date: IsoDate;
  items: NightlyItem[];
  checks: NightlyCheck[];
}

export interface WeekPerson {
  memberId: string;
  chores: number;
  routineSteps: number;
  earned: number;
}

/** The last seven days at home, counted from household data (no AI). */
export interface WeekSummary {
  from: IsoDate;
  to: IsoDate;
  people: WeekPerson[];
  choresDone: number;
  tasksDone: number;
  dinnersPlanned: number;
  dinnersNextWeek: number;
  nightsAllSet: number;
  notesAdded: number;
  /** The chore done most often this week. */
  topChore: { title: string; count: number } | null;
}

export const HOME_LIMITS = { noteText: 280, notes: 60, countdownTitle: 60, countdowns: 40, nightlyLabel: 60, nightlyItems: 20 } as const;

export const DEFAULT_NIGHTLY: { label: string; icon: string }[] = [
  { label: "Doors locked", icon: "🔒" },
  { label: "Garage closed", icon: "🚗" },
  { label: "Stove and oven off", icon: "🍳" },
  { label: "Backpacks and lunches ready", icon: "🎒" },
  { label: "Phones and tablets charging", icon: "🔌" },
];

export class HomeInputError extends Error {}

function text(v: unknown, max: number, what: string): string {
  const s = typeof v === "string" ? v.replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, "").trim() : "";
  if (!s) throw new HomeInputError(`${what} can’t be empty`);
  if (s.length > max) throw new HomeInputError(`${what} is too long (${max} characters at most)`);
  return s;
}

/** One emoji (or a short symbol); falls back to the default. */
function emoji(v: unknown, fallback: string): string {
  if (typeof v !== "string" || !v.trim()) return fallback;
  const s = v.trim();
  if ([...s].length > 4 || /[<>]/.test(s)) throw new HomeInputError("Pick a single emoji");
  return s;
}

export function parseNoteInput(body: Record<string, unknown>): NoteInput {
  const color = NOTE_COLORS.includes(body.color as NoteColor) ? (body.color as NoteColor) : "yellow";
  const forMemberId = typeof body.forMemberId === "string" && body.forMemberId ? body.forMemberId : null;
  const expiresOn = body.expiresOn === null || body.expiresOn === undefined || body.expiresOn === "" ? null : body.expiresOn;
  if (expiresOn !== null && !isCivilDate(expiresOn)) throw new HomeInputError("Invalid date");
  return { text: text(body.text, HOME_LIMITS.noteText, "The note"), color, forMemberId, expiresOn };
}

export function parseCountdownInput(body: Record<string, unknown>): CountdownInput {
  if (!isCivilDate(body.date)) throw new HomeInputError("Pick a date");
  return { title: text(body.title, HOME_LIMITS.countdownTitle, "The name"), date: body.date, emoji: emoji(body.emoji, "🎉") };
}

export function parseNightlyItem(body: Record<string, unknown>): { label: string; icon: string } {
  return { label: text(body.label, HOME_LIMITS.nightlyLabel, "The item"), icon: emoji(body.icon, "✓") };
}

/** Whole days from `today` to `date` (0 = today, negative = past). */
export function daysUntil(date: IsoDate, today: IsoDate): number {
  const a = parseIsoDate(today);
  const b = parseIsoDate(date);
  return Math.round((Date.UTC(b.y, b.m - 1, b.day) - Date.UTC(a.y, a.m - 1, a.day)) / 86_400_000);
}

/** "3 sleeps", "1 sleep", "Tonight's the night" (tomorrow), "Today!" */
export function sleepsLabel(date: IsoDate, today: IsoDate): string {
  const n = daysUntil(date, today);
  if (n < 0) return "Done";
  if (n === 0) return "Today!";
  return n === 1 ? "1 sleep" : `${n} sleeps`;
}

/** Countdowns still to come (today included), soonest first. */
export function upcomingCountdowns<T extends { date: IsoDate }>(list: T[], today: IsoDate): T[] {
  return list.filter((c) => compareIsoDate(c.date, today) >= 0).sort((a, b) => compareIsoDate(a.date, b.date));
}

/**
 * Which evening a moment belongs to for the nightly checklist: after
 * midnight and before 4 a.m. it is still last night.
 */
export function nightOf(localDate: IsoDate, localMinutes: number): IsoDate {
  return localMinutes < 4 * 60 ? addDays(localDate, -1) : localDate;
}

/** Notes not yet past their "until" date. */
export function visibleNotes<T extends { expiresOn: IsoDate | null }>(notes: T[], today: IsoDate): T[] {
  return notes.filter((n) => !n.expiresOn || compareIsoDate(n.expiresOn, today) >= 0);
}
